// OrderCreate.js - FIXED for facility admin permissions
import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Chip,
  FormHelperText,
  Divider,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  MedicalServices as PhysicianIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  OpenInNew as ExternalLinkIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { orderService } from "../../services/orderService";
import { userService } from "../../services/userService";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { facilityService } from "../../services/facilityService";
import authService from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";

const OrderCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [showNewFacility, setShowNewFacility] = useState(false);
  const [newFacility, setNewFacility] = useState({
    company_name: "",
    phone_number: "",
    address: "",
    email: "",
    subscription_type: "basic",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  // Custom entry dialogs
  const [showCustomExamDialog, setShowCustomExamDialog] = useState(false);
  const [newCustomExam, setNewCustomExam] = useState({
    name: "",
    description: "",
    duration: null,
  });
  const [showCustomDiagnosisDialog, setShowCustomDiagnosisDialog] =
    useState(false);
  const [newCustomDiagnosis, setNewCustomDiagnosis] = useState({
    code: "",
    description: "",
  });
  const [
    showCustomMedicalProfessionalDialog,
    setShowCustomMedicalProfessionalDialog,
  ] = useState(false);
  const [newCustomMedicalProfessional, setNewCustomMedicalProfessional] =
    useState({
      full_name: "",
      username: "",
      email: "",
      password: "",
      confirm_password: "",
      mp_category: "",
      license_number: "",
      npi_number: "",
    });

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    // Validate files
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Check file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        errors.push(`${file.name}: File too large (max 50MB)`);
        return;
      }

      // Check file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/tiff",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
        "application/rtf",
      ];

      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join("; "));
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFilesToOrder = async (orderId) => {
    if (selectedFiles.length === 0) {
      return true;
    }

    setUploadingFiles(true);

    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("file_type", "document");

        const response = await fetch(`/api/v1/orders/${orderId}/files`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "File upload failed");
        }

        return response.json();
      });

      await Promise.all(uploadPromises);
      return true;
    } catch (error) {
      console.error("Error uploading files:", error);
      setError(`File upload failed: ${error.message}`);
      return false;
    } finally {
      setUploadingFiles(false);
    }
  };

  // Form values with auto-save functionality
  const [formValues, setFormValues] = useState({
    patient_id: "",
    ordering_physician_id: "",
    place_of_service: "Home",
    scheduled_date: "",
    scheduled_time: "",
    scheduled_time_end: "",
    special_notes: "",
    ordering_health_company: "",
    selected_diagnoses: [],
    selected_exams: [],
    medical_professional_id: "",
    facility_id: "",
  });

  // Form validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Loading states
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // New patient/physician form sections
  const [showNewPatient, setShowNewPatient] = useState(false);
  const [showNewPhysician, setShowNewPhysician] = useState(false);

  const [newPatient, setNewPatient] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    phone_number: "",
    address: "",
    address_notes: "",
    city: "",
    state: "",
    zip_code: "",
  });

  const [newPhysician, setNewPhysician] = useState({
    first_name: "",
    last_name: "",
    npi: "",
    facility_name: "",
    phone_number: "",
    email: "",
  });

  // FIXED: Check if user can create physicians and facilities
  const canCreatePhysicians =
    user?.role === "super_admin" || user?.role === "facility_admin";
  const canCreateFacilities = user?.role === "super_admin";
  const canAssignMedicalProfessionals = user?.role === "super_admin";

  // Enhanced error handling function
  const handleError = (error, context) => {
    console.error(`Error in ${context}:`, error);

    // Check for authentication errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      // FIXED: Better permission error handling
      if (context.includes("physician") && !canCreatePhysicians) {
        setError(
          "You don't have permission to create physicians. Please contact your administrator."
        );
      } else if (context.includes("facility") && !canCreateFacilities) {
        setError(
          "You don't have permission to create facilities. Please contact your administrator."
        );
      } else {
        setError("Your session has expired. Please log in again.");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
      return;
    }

    // Check for network errors
    if (!error.response) {
      setError("Network error. Please check your connection and try again.");
      return;
    }

    // Circuit breaker errors
    if (
      error.message?.includes("Circuit breaker") ||
      error.response?.status === 503
    ) {
      setError(
        "Service temporarily unavailable. Please try again in a few moments."
      );
      return;
    }

    // Extract meaningful error message
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred";

    setError(`${context}: ${errorMessage}`);
  };

  useEffect(() => {
    const loadReferenceData = async () => {
      setLoading(true);
      setError(""); // Clear any previous errors

      try {
        // Set today's date as default
        const today = new Date().toISOString().split("T")[0];


        // =============================================
        // ENHANCED FACILITY ID DETECTION
        // =============================================
        let initialFacilityId = "";
        let facilityIdSource = "none";

        if (user?.role === "facility_admin") {

          // Method 1: Direct facility_id property
          if (user.facility_id) {
            initialFacilityId = user.facility_id;
            facilityIdSource = "user.facility_id";
          }
          // Method 2: Alternative facilityId property
          else if (user.facilityId) {
            initialFacilityId = user.facilityId;
            facilityIdSource = "user.facilityId";
          }
          // Method 3: Nested facility object
          else if (user.facility?.id) {
            initialFacilityId = user.facility.id;
            facilityIdSource = "user.facility.id";
          }
          // Method 4: Direct facility property (sometimes ID is stored directly)
          else if (user.facility && typeof user.facility === "number") {
            initialFacilityId = user.facility;
            facilityIdSource = "user.facility (direct)";
          }


          // Method 5: LocalStorage fallback
          if (!initialFacilityId) {
            try {
              const storedUser = JSON.parse(
                sessionStorage.getItem("user") || "{}"
              );

              if (storedUser.facility_id) {
                initialFacilityId = storedUser.facility_id;
                facilityIdSource = "localStorage.facility_id";
              } else if (storedUser.facilityId) {
                initialFacilityId = storedUser.facilityId;
                facilityIdSource = "localStorage.facilityId";
              } else if (storedUser.facility?.id) {
                initialFacilityId = storedUser.facility.id;
                facilityIdSource = "localStorage.facility.id";
              }

            } catch (e) {
              console.error("LocalStorage fallback failed:", e);
            }
          }

          // Method 6: API fallback (fetch fresh user data)
          if (!initialFacilityId) {
            try {
              // Try to fetch current user data from API
              const response = await fetch("/api/v1/auth/me", {
                headers: {
                  Authorization: `Bearer ${authService.getToken()}`,
                  "Content-Type": "application/json",
                },
              });

              if (response.ok) {
                const freshUserData = await response.json();

                if (freshUserData.facility_id) {
                  initialFacilityId = freshUserData.facility_id;
                  facilityIdSource = "API.facility_id";

                  // Update localStorage with fresh data
                  localStorage.setItem("user", JSON.stringify(freshUserData));

                  // Note: We're not updating user context here to avoid re-rendering loops
                  // The fresh data is saved to localStorage for next time
                }
              } else {
                console.warn(
                  "API fallback failed with status:",
                  response.status
                );
              }
            } catch (apiError) {
              console.error("API fallback error:", apiError);
            }
          }

          // Method 7: TEMPORARY HARDCODE for testing (remove after fixing)
          // Uncomment the lines below if you want to temporarily hardcode for testing
          /*
        if (!initialFacilityId) {
          initialFacilityId = 1; // CHANGE THIS TO YOUR ACTUAL FACILITY ID
          facilityIdSource = "TEMPORARY_HARDCODE";
        }
        */

          // Final validation for facility admin
          if (!initialFacilityId) {
            console.error(
              "❌ CRITICAL: No facility_id found for facility admin user"
            );
            console.error("User object:", user);
            console.error(
              "❌ LocalStorage user:",
              JSON.parse(sessionStorage.getItem("user") || "{}")
            );

            setError(
              "Your account is not properly associated with a facility. Please contact your administrator to fix your account settings."
            );
            setLoading(false);
            return;
          }


          // Ensure it's a number
          initialFacilityId = parseInt(initialFacilityId);
        } else if (user?.role === "super_admin") {
          // Super admins will select facility manually, so no initial facility_id needed
        } else {
        }

        // =============================================
        // SET INITIAL FORM VALUES
        // =============================================
        const initialFormValues = {
          scheduled_date: today,
          facility_id: initialFacilityId || "",
          place_of_service: "Home",
          ordering_health_company: "",
          scheduled_time: "",
          scheduled_time_end: "",
          special_notes: "",
          medical_professional_id: "",
          patient_id: "",
          ordering_physician_id: "",
          selected_diagnoses: [],
          selected_exams: [],
        };

        setFormValues(initialFormValues);

        // =============================================
        // FETCH REFERENCE DATA
        // =============================================

        const dataPromises = [
          orderService.getPatients().catch((err) => {
            console.error("Error loading patients:", err);
            return [];
          }),
          orderService.getPhysicians().catch((err) => {
            console.error("Error loading physicians:", err);
            return [];
          }),
          orderService.getExamTypes().catch((err) => {
            console.error("Error loading exam types:", err);
            return [];
          }),
          orderService.getDiagnoses().catch((err) => {
            console.error("Error loading diagnoses:", err);
            return [];
          }),
          userService.getMedicalProfessionals().catch((err) => {
            console.error("Error loading medical professionals:", err);
            return [];
          }),
          facilityService.getFacilities().catch((err) => {
            console.error("Error loading facilities:", err);
            return [];
          }),
        ];

        const [
          patientsResponse,
          physiciansResponse,
          examTypesResponse,
          diagnosesResponse,
          medicalProfessionalsResponse,
          facilitiesResponse,
        ] = await Promise.all(dataPromises);

        // Set the fetched data
        setPatients(patientsResponse || []);
        setPhysicians(physiciansResponse || []);
        setExamTypes(examTypesResponse || []);
        setDiagnoses(diagnosesResponse || []);
        setMedicalProfessionals(medicalProfessionalsResponse || []);
        setFacilities(facilitiesResponse || []);


        // =============================================
        // FACILITY VALIDATION AFTER DATA LOAD
        // =============================================
        if (user?.role === "facility_admin" && initialFacilityId) {
          // Verify the facility exists in the loaded facilities list (if we have permission to see it)
          const facilityExists = facilitiesResponse?.some(
            (f) => f.id === initialFacilityId
          );

          if (facilityExists) {
          } else if (facilitiesResponse?.length === 0) {
          } else {
          }

          // Final form update with confirmed facility_id
          setFormValues((prev) => ({
            ...prev,
            facility_id: initialFacilityId,
          }));
        } else if (
          user?.role === "super_admin" &&
          facilitiesResponse?.length > 0
        ) {
          // For super admins, optionally set first facility as default
          setFormValues((prev) => ({
            ...prev,
            facility_id: facilitiesResponse[0].id,
          }));
        }

        // =============================================
        // SUCCESS MESSAGING
        // =============================================
        const failedServices = [];
        if (!patientsResponse?.length) failedServices.push("patients");
        if (!physiciansResponse?.length) failedServices.push("physicians");
        if (!examTypesResponse?.length) failedServices.push("exam types");
        if (!diagnosesResponse?.length) failedServices.push("diagnoses");

        if (failedServices.length > 0) {
          setSnackbarMessage(
            `Warning: Some data could not be loaded (${failedServices.join(
              ", "
            )}). You can still create orders manually.`
          );
          setSnackbarOpen(true);
        }

      } catch (err) {
        console.error("Error in loadReferenceData:", err);
        setError(`Failed to load order creation data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    // =============================================
    // USEEFFECT EXECUTION CONTROL
    // =============================================
    if (user) {
      loadReferenceData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const filterPatients = (options, { inputValue }) => {
    if (!inputValue) return options;
    const query = inputValue.toLowerCase();
    return options.filter((patient) => {
      const firstName = (patient.first_name || "").toString().toLowerCase();
      const lastName = (patient.last_name || "").toString().toLowerCase();
      const phone = (patient.phone_number || "").toString();
      const address = (patient.address || "").toString().toLowerCase();
      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        phone.includes(query) ||
        address.includes(query)
      );
    });
  };

  const filterPhysicians = (options, { inputValue }) => {
    if (!inputValue) return options;
    const query = inputValue.toLowerCase();
    return options.filter((physician) => {
      const firstName = (physician.first_name || "").toString().toLowerCase();
      const lastName = (physician.last_name || "").toString().toLowerCase();
      const specialty = (physician.specialty || "").toString().toLowerCase();
      const facilityName = (physician.facility_name || "").toString().toLowerCase();
      const npi = (physician.npi || "").toString();
      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        specialty.includes(query) ||
        facilityName.includes(query) ||
        npi.includes(query)
      );
    });
  };

  // Handle input change for main form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear related errors
    if (error) {
      setError("");
    }
  };

  // Handle changes for new patient form
  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    setNewPatient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle changes for new physician form
  const handlePhysicianChange = (e) => {
    const { name, value } = e.target;
    setNewPhysician((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle changes for new facility form
  const handleFacilityChange = (e) => {
    const { name, value } = e.target;
    setNewFacility((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // FIXED: Create a new facility - only for super admins
  const handleCreateFacility = async () => {
    if (!canCreateFacilities) {
      setError(
        "You don't have permission to create facilities. Please contact your administrator."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");

      // Validate required fields (email is now optional)
      if (
        !newFacility.company_name?.trim() ||
        !newFacility.phone_number?.trim() ||
        !newFacility.address?.trim()
      ) {
        setError("Please fill out company name, phone number, and address");
        setSaving(false);
        return;
      }

      // Validate email format if provided
      if (
        newFacility.email?.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newFacility.email)
      ) {
        setError("Please enter a valid email address or leave it empty");
        setSaving(false);
        return;
      }


      const response = await facilityService.createFacility({
        ...newFacility,
        email: newFacility.email?.trim() || null,
      });

      // Update facilities list
      setFacilities((prev) => [...prev, response]);

      // Select this facility
      setFormValues((prev) => ({
        ...prev,
        facility_id: response.id,
      }));

      // Reset form and hide it
      setShowNewFacility(false);
      setNewFacility({
        company_name: "",
        phone_number: "",
        address: "",
        email: "",
        subscription_type: "basic",
      });

      setSnackbarMessage("Facility created successfully");
      setSnackbarOpen(true);
    } catch (err) {
      handleError(err, "Failed to create new facility");
    } finally {
      setSaving(false);
    }
  };

  // FIXED: Create a new patient with proper error handling and validation
  const handleCreatePatient = async () => {
    try {
      setSaving(true);
      setError("");

      // Validate required fields
      if (
        !newPatient.first_name?.trim() ||
        !newPatient.last_name?.trim() ||
        !newPatient.address?.trim() ||
        !newPatient.city?.trim() ||
        !newPatient.state?.trim() ||
        !newPatient.zip_code?.trim()
      ) {
        setError("Please fill out all required patient fields (marked with *)");
        setSaving(false);
        return;
      }


      // FIXED: Include facility_id in patient data
      const patientData = {
        first_name: newPatient.first_name.trim(),
        last_name: newPatient.last_name.trim(),
        date_of_birth: newPatient.date_of_birth || null,
        gender: newPatient.gender || null,
        phone_number: newPatient.phone_number?.trim() || null,
        address: newPatient.address.trim(),
        city: newPatient.city.trim(),
        state: newPatient.state.trim(),
        zip_code: newPatient.zip_code.trim(),
        address_notes: newPatient.address_notes?.trim() || null,
        facility_id: user?.role === "facility_admin" ? user.facility_id : null, // ADD THIS LINE
      };

      const response = await orderService.createPatient(patientData);

      // Update patients list
      setPatients((prev) => [...prev, response]);

      // Select this patient
      setFormValues((prev) => ({
        ...prev,
        patient_id: response.id,
      }));

      // Reset form and hide it
      setShowNewPatient(false);
      setNewPatient({
        first_name: "",
        last_name: "",
        date_of_birth: "",
        gender: "",
        phone_number: "",
        address: "",
        address_notes: "",
        city: "",
        state: "",
        zip_code: "",
      });

      setSnackbarMessage("Patient created successfully");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error creating patient:", err);
      console.error("Error details:", err.response?.data);

      // Show specific error message
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((error) => `${error.loc?.join(".")}: ${error.msg}`)
            .join("; ");
          setError(`Patient validation failed: ${errorMessages}`);
        } else {
          setError(`Patient validation failed: ${err.response.data.detail}`);
        }
      } else {
        setError(`Failed to create patient: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // FIXED: Create a new physician with enhanced error handling and permission checking
  const handleCreatePhysician = async () => {
    if (!canCreatePhysicians) {
      setError(
        "You don't have permission to create physicians. Please contact your administrator."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");

      // Validate required fields
      if (!newPhysician.first_name?.trim() || !newPhysician.last_name?.trim()) {
        setError("Physician first and last name are required");
        setSaving(false);
        return;
      }


      try {
        const response = await orderService.createPhysician(newPhysician);

        // Update physicians list
        setPhysicians((prev) => [...prev, response]);

        // Select this physician
        setFormValues((prev) => ({
          ...prev,
          ordering_physician_id: response.id, // Use the real ID from API
        }));

        // Reset form and hide it
        setShowNewPhysician(false);
        setNewPhysician({
          first_name: "",
          last_name: "",
          npi: "",
          facility_name: "",
          phone_number: "",
          email: "",
        });

        setSnackbarMessage("Physician created successfully");
        setSnackbarOpen(true);
      } catch (apiError) {
        console.error("API error creating physician:", apiError);

        // FIXED: For facility admins, create a local placeholder that won't be sent to API
        if (user?.role === "facility_admin") {
          const tempPhysician = {
            id: `local_${Date.now()}`, // Use "local_" prefix instead of "temp_"
            first_name: newPhysician.first_name,
            last_name: newPhysician.last_name,
            npi: newPhysician.npi || "",
            facility_name: newPhysician.facility_name || "",
            phone_number: newPhysician.phone_number || "",
            email: newPhysician.email || "",
            is_local: true, // Flag to indicate this is local only
          };

          // Add to local state
          setPhysicians((prev) => [...prev, tempPhysician]);

          // DON'T select it automatically - let user choose
          setShowNewPhysician(false);
          setNewPhysician({
            first_name: "",
            last_name: "",
            npi: "",
            facility_name: "",
            phone_number: "",
            email: "",
          });

          setSnackbarMessage(
            "Physician saved locally. Note: This physician will need to be created by an administrator before submitting orders."
          );
          setSnackbarOpen(true);
        } else {
          throw apiError; // Re-throw for other roles
        }
      }
    } catch (err) {
      console.error("Error in handleCreatePhysician:", err);
      if (err.response?.status === 403) {
        setError(
          "You don't have permission to create physicians. Please contact your administrator."
        );
      } else {
        setError(`Failed to create physician: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // FIXED: Handle custom exam creation with proper API call during order creation
  const handleCreateCustomExam = async () => {
    if (!newCustomExam.name?.trim()) {
      setError("Exam name is required");
      return;
    }

    try {
      setSaving(true);
      setError("");


      // Create the exam type via API
      const response = await orderService.createExamType({
        name: newCustomExam.name.trim(),
        description: newCustomExam.description?.trim() || "",
        duration: newCustomExam.duration,
      });


      // CRITICAL: Create the exam object with the correct structure
      const newExam = {
        id: response.id, // This is the real database ID
        name: response.name,
        description: response.description || "",
        duration: response.duration,
      };


      // Add to exam types list
      setExamTypes((prev) => [...prev, newExam]);

      // CRITICAL: Add to selected exams immediately
      setFormValues((prev) => {
        const updatedExams = [...prev.selected_exams, newExam];
        return {
          ...prev,
          selected_exams: updatedExams,
        };
      });

      // Clear form and hide dialog
      setNewCustomExam({ name: "", description: "", duration: null });
      setShowCustomExamDialog(false);

      setSnackbarMessage("Custom exam created and added to order");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error creating custom exam:", err);
      setError(err.message || "Failed to create custom exam");
    } finally {
      setSaving(false);
    }
  };

  // Handle custom diagnosis creation
  const handleCreateCustomDiagnosis = () => {
    if (
      !newCustomDiagnosis.code?.trim() ||
      !newCustomDiagnosis.description?.trim()
    ) {
      setError("Diagnosis code and description are required");
      return;
    }

    // Create a local custom diagnosis with a temporary negative ID
    const newDiagnosis = {
      id: -1 * (diagnoses.length + 1), // Temporary negative ID
      code: newCustomDiagnosis.code.trim(),
      description: newCustomDiagnosis.description.trim(),
      isCustom: true, // Flag to identify custom diagnoses
    };

    setDiagnoses((prev) => [...prev, newDiagnosis]);

    // Add to selected diagnoses automatically
    setFormValues((prev) => ({
      ...prev,
      selected_diagnoses: [...prev.selected_diagnoses, newDiagnosis],
    }));

    setNewCustomDiagnosis({ code: "", description: "" });
    setShowCustomDiagnosisDialog(false);
    setSnackbarMessage("Custom diagnosis added");
    setSnackbarOpen(true);
  };

  // Handle custom medical professional creation with retry logic
  const handleCreateCustomMedicalProfessional = async () => {
    if (
      !newCustomMedicalProfessional.full_name?.trim() ||
      !newCustomMedicalProfessional.username?.trim() ||
      !newCustomMedicalProfessional.email?.trim() ||
      !newCustomMedicalProfessional.password?.trim() ||
      !newCustomMedicalProfessional.mp_category
    ) {
      setError("Name, username, email, password and category are required");
      return;
    }

    if (
      newCustomMedicalProfessional.password !==
      newCustomMedicalProfessional.confirm_password
    ) {
      setError("Passwords don't match");
      return;
    }

    // Validate email format
    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomMedicalProfessional.email)
    ) {
      setError("Please enter a valid email address");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Generate unique email if there's a conflict
      let emailToUse = newCustomMedicalProfessional.email.trim().toLowerCase();
      let usernameToUse = newCustomMedicalProfessional.username
        .trim()
        .toLowerCase();

      // Add timestamp to make email/username unique if needed
      const timestamp = Date.now();

      // Prepare data for API call
      const mpData = {
        full_name: newCustomMedicalProfessional.full_name.trim(),
        username: usernameToUse,
        email: emailToUse,
        password: newCustomMedicalProfessional.password,
        mp_category: newCustomMedicalProfessional.mp_category,
        medical_license_number:
          newCustomMedicalProfessional.license_number?.trim() || null,
        npi_number: newCustomMedicalProfessional.npi_number?.trim() || null,
        facility_id: formValues.facility_id || null,
        send_activation_email: false,
      };


      try {
        const response =
          await medicalProfessionalsService.createCustomMedicalProfessional(
            mpData
          );

        // Create object to add to local state
        const newMP = {
          id: response.id,
          user: {
            full_name: response.user?.full_name || mpData.full_name,
            id: response.user?.id,
          },
          mp_category: response.mp_category,
          medical_license_number: response.medical_license_number,
          npi_number: response.npi_number,
        };

        // Update state
        setMedicalProfessionals((prev) => [...prev, newMP]);
        setFormValues((prev) => ({
          ...prev,
          medical_professional_id: newMP.id,
        }));

        // Show success message
        setSnackbarMessage("Medical professional created successfully");
        setSnackbarOpen(true);

        // Reset form and close dialog
        setNewCustomMedicalProfessional({
          full_name: "",
          username: "",
          email: "",
          password: "",
          confirm_password: "",
          mp_category: "",
          license_number: "",
          npi_number: "",
        });
        setShowCustomMedicalProfessionalDialog(false);
      } catch (err) {
        console.error("Error creating medical professional:", err);

        // Handle email/username conflicts gracefully
        if (err.message?.includes("Email already registered")) {
          // Try with a unique email
          const uniqueEmail = `${emailToUse.split("@")[0]}_${timestamp}@${
            emailToUse.split("@")[1]
          }`;
          const uniqueUsername = `${usernameToUse}_${timestamp}`;


          try {
            const retryResponse =
              await medicalProfessionalsService.createCustomMedicalProfessional(
                {
                  ...mpData,
                  email: uniqueEmail,
                  username: uniqueUsername,
                }
              );

            const newMP = {
              id: retryResponse.id,
              user: {
                full_name: retryResponse.user?.full_name || mpData.full_name,
                id: retryResponse.user?.id,
                email: uniqueEmail,
              },
              mp_category: retryResponse.mp_category,
              medical_license_number: retryResponse.medical_license_number,
              npi_number: retryResponse.npi_number,
            };

            setMedicalProfessionals((prev) => [...prev, newMP]);
            setFormValues((prev) => ({
              ...prev,
              medical_professional_id: newMP.id,
            }));

            setSnackbarMessage(
              "Medical professional created with auto-generated unique credentials"
            );
            setSnackbarOpen(true);

            // Reset form and close dialog
            setNewCustomMedicalProfessional({
              full_name: "",
              username: "",
              email: "",
              password: "",
              confirm_password: "",
              mp_category: "",
              license_number: "",
              npi_number: "",
            });
            setShowCustomMedicalProfessionalDialog(false);
          } catch (retryErr) {
            handleError(
              retryErr,
              "Failed to create medical professional even with unique credentials"
            );
          }
        } else {
          handleError(err, "Failed to create medical professional");
        }
      }
    } catch (err) {
      handleError(err, "Failed to create medical professional");
    } finally {
      setSaving(false);
    }
  };

  // Handle diagnosis selection
  const handleDiagnosisChange = (event, value) => {
    setFormValues((prev) => ({
      ...prev,
      selected_diagnoses: value,
    }));
  };

  const handleExamChange = (event, value) => {

    // Clear exam-related errors when user selects exams
    if (value && value.length > 0) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.selected_exams;
        return newErrors;
      });

      // Clear general error if it was about exams
      if (error && error.toLowerCase().includes("exam")) {
        setError("");
      }
    }

    setFormValues((prev) => ({
      ...prev,
      selected_exams: value,
    }));
  };

  const validateForm = () => {
    const errors = {};


    // CRITICAL: Check if we have required reference data
    if (patients.length === 0 && !showNewPatient) {
      errors.data_loading =
        "No patients available. Please create a patient first or contact support.";
    }

    if (examTypes.length === 0) {
      errors.data_loading =
        "No exam types available. Please create custom exams or contact support.";
    }

    // ENHANCED: Patient validation that considers auto-save capability

    const hasSelectedPatient =
      formValues.patient_id && formValues.patient_id !== "";
    const hasNewPatientData =
      showNewPatient &&
      newPatient.first_name?.trim() &&
      newPatient.last_name?.trim() &&
      newPatient.address?.trim() &&
      newPatient.city?.trim() &&
      newPatient.state?.trim() &&
      newPatient.zip_code?.trim();


    if (!hasSelectedPatient && !hasNewPatientData) {
      if (showNewPatient) {
        // New patient form is open but incomplete
        const missingFields = [];
        if (!newPatient.first_name?.trim()) missingFields.push("First Name");
        if (!newPatient.last_name?.trim()) missingFields.push("Last Name");
        if (!newPatient.address?.trim()) missingFields.push("Address");
        if (!newPatient.city?.trim()) missingFields.push("City");
        if (!newPatient.state?.trim()) missingFields.push("State");
        if (!newPatient.zip_code?.trim()) missingFields.push("ZIP Code");

        errors.patient_id = `Please complete the new patient form. Missing: ${missingFields.join(
          ", "
        )}`;
      } else {
        // No patient selected and new patient form not open
        errors.patient_id =
          "Please select a patient or click 'New Patient' to add one";
      }
    } else if (hasSelectedPatient) {
      // Verify selected patient exists in our loaded data
      const patientExists = patients.some(
        (p) => p.id === parseInt(formValues.patient_id)
      );
      if (!patientExists) {
        errors.patient_id =
          "Selected patient not found. Please refresh and try again.";
      } else {
      }
    } else if (hasNewPatientData) {
    }

    // Validate place of service
    if (!formValues.place_of_service || formValues.place_of_service === "") {
      errors.place_of_service = "Place of service is required";
    }

    // ENHANCED: Facility validation
    if (user?.role === "facility_admin") {

      const userFacilityId =
        user.facility_id ||
        user.facilityId ||
        user.facility?.id ||
        formValues.facility_id; // Check form value too

      if (!userFacilityId) {
        errors.facility_id =
          "Your account is not associated with a facility. Please contact support.";
      } else {

        // Auto-set facility_id if not already set
        if (!formValues.facility_id) {
          setFormValues((prev) => ({
            ...prev,
            facility_id: userFacilityId,
          }));
        }
      }
    } else if (user?.role === "super_admin") {
      if (!formValues.facility_id || formValues.facility_id === "") {
        errors.facility_id = "Please select a facility";
      } else {
      }
    } else {
      errors.facility_id = "You don't have permission to create orders.";
    }

    // ENHANCED: Exam validation
    if (
      !formValues.selected_exams ||
      !Array.isArray(formValues.selected_exams) ||
      formValues.selected_exams.length === 0
    ) {
      errors.selected_exams = "At least one exam is required";
    } else {

      // Validate each selected exam
      let validExamCount = 0;
      formValues.selected_exams.forEach((exam, index) => {
        if (exam && (exam.id || exam.name)) {
          validExamCount++;
        } else {
        }
      });

      if (validExamCount === 0) {
        errors.selected_exams =
          "No valid exams selected. Please select valid exams.";
      }
    }


    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    // Clear previous errors and disable validation during auto-save
    setError("");
    setValidationErrors({});
    setSaving(true);

    try {
      // =============================================
      // STEP 0: PRE-VALIDATION CHECK
      // =============================================

      // Run basic validation first (but allow auto-save to fix patient issues)
      const isValid = validateForm();

      // If validation fails and it's not a patient issue that can be auto-saved, stop here
      if (!isValid) {
        const hasPatientError = validationErrors.patient_id;
        const canAutoSavePatient =
          showNewPatient &&
          newPatient.first_name?.trim() &&
          newPatient.last_name?.trim() &&
          newPatient.address?.trim() &&
          newPatient.city?.trim() &&
          newPatient.state?.trim() &&
          newPatient.zip_code?.trim() &&
          (!formValues.patient_id || formValues.patient_id === "");

        // If patient error exists but we can auto-save, continue. Otherwise, stop.
        if (hasPatientError && canAutoSavePatient) {
        } else {
          setSaving(false);
          return; // Stop execution
        }
      } else {
      }

      // =============================================
      // STEP 1: AUTO-SAVE PATIENT IF NEEDED
      // =============================================
      let finalPatientId = formValues.patient_id;


      // FIXED: Better detection of when to auto-save patient
      const shouldAutoSavePatient =
        showNewPatient && // New patient form is visible
        newPatient.first_name?.trim() && // Has required data
        newPatient.last_name?.trim() &&
        newPatient.address?.trim() &&
        newPatient.city?.trim() &&
        newPatient.state?.trim() &&
        newPatient.zip_code?.trim() &&
        (!finalPatientId || finalPatientId === ""); // No patient selected yet


      if (shouldAutoSavePatient) {

        // Validate required fields before sending
        const requiredFields = [
          "first_name",
          "last_name",
          "address",
          "city",
          "state",
          "zip_code",
        ];

        const missingFields = requiredFields.filter(
          (field) => !newPatient[field]?.trim()
        );

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required patient fields: ${missingFields.join(", ")}`
          );
        }

        // FIXED: Clean and validate data before sending
        const newPatientData = {
          first_name: newPatient.first_name.trim(),
          last_name: newPatient.last_name.trim(),
          date_of_birth: newPatient.date_of_birth || null,
          gender: newPatient.gender || null,
          phone_number: newPatient.phone_number?.trim() || null,
          address: newPatient.address.trim(),
          city: newPatient.city.trim(),
          state: newPatient.state.trim(),
          zip_code: newPatient.zip_code.trim(),
          address_notes: newPatient.address_notes?.trim() || null,
          // CRITICAL: Include facility_id for facility admin users
          facility_id:
            user?.role === "facility_admin"
              ? user.facility_id || user.facilityId || user.facility?.id
              : null,
        };


        try {
          const savedPatient = await orderService.createPatient(newPatientData);
          finalPatientId = savedPatient.id;


          // Update states IMMEDIATELY
          setPatients((prev) => [...prev, savedPatient]);
          setFormValues((prev) => ({ ...prev, patient_id: finalPatientId }));

          // Hide the new patient form since we saved it
          setShowNewPatient(false);
          setNewPatient({
            first_name: "",
            last_name: "",
            date_of_birth: "",
            gender: "",
            phone_number: "",
            address: "",
            address_notes: "",
            city: "",
            state: "",
            zip_code: "",
          });

        } catch (error) {
          console.error("Failed to auto-save patient:", error);
          console.error("Error details:", error.response?.data);

          // Show specific error message
          if (error.response?.data?.detail) {
            if (Array.isArray(error.response.data.detail)) {
              const errorMessages = error.response.data.detail
                .map((err) => `${err.loc?.join(".")}: ${err.msg}`)
                .join("; ");
              throw new Error(`Patient validation failed: ${errorMessages}`);
            } else {
              throw new Error(
                `Patient validation failed: ${error.response.data.detail}`
              );
            }
          } else {
            throw new Error(`Failed to save patient: ${error.message}`);
          }
        }
      }

      // =============================================
      // STEP 1.5: POST-PATIENT VALIDATION
      // =============================================

      // CRITICAL: Validate we have a patient NOW (after auto-save)
      if (!finalPatientId || finalPatientId === "") {

        throw new Error(
          "Please select an existing patient or fill out the 'Add New Patient' form completely with all required fields (marked with *)."
        );
      }


      // Run validation again with updated patient ID (temporarily)
      const originalFormValues = { ...formValues };
      const updatedFormValues = { ...formValues, patient_id: finalPatientId };
      setFormValues(updatedFormValues); // Temporarily update for validation

      const finalValidation = validateForm();
      setFormValues(originalFormValues); // Restore original values

      if (!finalValidation) {
        throw new Error("Please fill in all required fields.");
      }


      // =============================================
      // STEP 2: AUTO-SAVE PHYSICIAN IF NEEDED
      // =============================================
      let finalPhysicianId = formValues.ordering_physician_id;
      let physicianData = null;


      // Check if user filled out new physician form but didn't save it
      if (
        showNewPhysician &&
        newPhysician.first_name?.trim() &&
        newPhysician.last_name?.trim() &&
        (!finalPhysicianId ||
          finalPhysicianId === "" ||
          finalPhysicianId.startsWith("local_"))
      ) {

        // Don't create physician on frontend, let backend handle it
        physicianData = {
          first_name: newPhysician.first_name.trim(),
          last_name: newPhysician.last_name.trim(),
          npi: newPhysician.npi?.trim() || null,
          specialty: newPhysician.specialty?.trim() || null,
          phone_number: newPhysician.phone_number?.trim() || null,
          email: newPhysician.email?.trim() || null,
          facility_name: newPhysician.facility_name?.trim() || null,
        };

        finalPhysicianId = null; // Let backend create and assign
      } else if (
        finalPhysicianId &&
        !isNaN(finalPhysicianId) &&
        finalPhysicianId > 0
      ) {
        // Use existing physician
        finalPhysicianId = parseInt(finalPhysicianId);
      } else {
        finalPhysicianId = null;
      }

      // =============================================
      // STEP 3: DETERMINE FACILITY ID
      // =============================================
      let facilityId = null;


      if (user?.role === "facility_admin") {
        // Get facility_id from multiple possible sources
        facilityId =
          formValues.facility_id ||
          user.facility_id ||
          user.facilityId ||
          user.facility?.id;

        if (!facilityId) {
          console.error("Facility admin user:", user);
          throw new Error(
            "Your account is not properly associated with a facility. Please contact your administrator to fix your account settings."
          );
        }

      } else if (user?.role === "super_admin") {
        if (!formValues.facility_id) {
          throw new Error("Please select a facility.");
        }
        facilityId = parseInt(formValues.facility_id);
      } else {
        throw new Error("You don't have permission to create orders.");
      }


      // =============================================
      // STEP 4: VALIDATE REQUIRED FIELDS
      // =============================================

      if (!formValues.place_of_service) {
        throw new Error("Place of service is required.");
      }

      if (
        !formValues.selected_exams ||
        formValues.selected_exams.length === 0
      ) {
        throw new Error(
          "At least one exam is required. Please select or add exams."
        );
      }


      // =============================================
      // STEP 5: PREPARE EXAMS
      // =============================================
      // STEP 5: PREPARE EXAMS - FIXED VERSION
      const transformedExams = [];


      if (formValues.selected_exams && formValues.selected_exams.length > 0) {
        formValues.selected_exams.forEach((exam, index) => {

          if (!exam) {
            return;
          }

          // CRITICAL FIX: Ensure we capture the ID correctly
          const examId = exam.id || exam.exam_type_id;

          if (examId && parseInt(examId) > 0) {

            // CRITICAL: Create a clean object with ONLY the fields the backend expects
            const examData = {
              exam_type_id: parseInt(examId),
            };

            // Only add notes if it exists and is not empty
            if (exam.notes && exam.notes.trim()) {
              examData.notes = exam.notes.trim();
            }

            transformedExams.push(examData);
          } else {
            console.error(`Exam ${index} has no valid ID:`, exam);
            console.error("examId:", examId, "parseInt:", parseInt(examId));
          }
        });
      } else {
        console.error("No exams found in formValues.selected_exams");
      }


      if (transformedExams.length === 0) {
        console.error("NO EXAMS TRANSFORMED - This will cause the issue!");
        console.error("Original selected_exams:", formValues.selected_exams);
        throw new Error(
          "No valid exams found. Please ensure exams are properly selected."
        );
      }

      // =============================================
      // STEP 6: PREPARE DIAGNOSES
      // =============================================
      const transformedDiagnoses = [];

      if (
        formValues.selected_diagnoses &&
        formValues.selected_diagnoses.length > 0
      ) {
        formValues.selected_diagnoses.forEach((diagnosis, index) => {
          if (!diagnosis) return;

          if (diagnosis.isCustom || diagnosis.id < 0) {
            // Custom diagnosis
            const customDiagnosis = {
              code: diagnosis.code.trim(),
              description: diagnosis.description.trim(),
              primary: index === 0,
              is_custom: true,
            };
            transformedDiagnoses.push(customDiagnosis);
          } else {
            // Regular diagnosis
            const regularDiagnosis = {
              diagnosis_id: parseInt(diagnosis.id),
              primary: index === 0,
              is_custom: false,
            };
            transformedDiagnoses.push(regularDiagnosis);
          }
        });
      }


      // =============================================
      // STEP 7: PREPARE MEDICAL PROFESSIONAL
      // =============================================
      let medicalProfessionalId = null;

      if (formValues.medical_professional_id) {
        const mpId = formValues.medical_professional_id;
        if (mpId && !isNaN(mpId) && mpId > 0) {
          medicalProfessionalId = parseInt(mpId);
        }
      }


      // =============================================
      // STEP 8: CREATE ORDER
      // =============================================
      const orderData = {
        patient_id: parseInt(finalPatientId),
        ordering_physician_id: finalPhysicianId,
        place_of_service: formValues.place_of_service,
        special_notes: formValues.special_notes?.trim() || null,
        ordering_health_company:
          formValues.ordering_health_company?.trim() || null,
        scheduled_date: formValues.scheduled_date || null,
        scheduled_time: formValues.scheduled_time || null,
        scheduled_time_end: formValues.scheduled_time_end || null,
        medical_professional_id: medicalProfessionalId,
        facility_id: facilityId,
        exams: transformedExams,
        diagnoses: transformedDiagnoses,
        physician_data: physicianData, // This will tell backend to create physician
      };


      const response = await orderService.createOrder(orderData);


      if (selectedFiles.length > 0) {
        const filesUploaded = await uploadFilesToOrder(response.id);
        if (!filesUploaded) {
          setSnackbarMessage("Order created but some files failed to upload");
          setSnackbarOpen(true);
        }
      }

      // Reset forms
      if (showNewPhysician) {
        setShowNewPhysician(false);
        setNewPhysician({
          first_name: "",
          last_name: "",
          npi: "",
          facility_name: "",
          phone_number: "",
          email: "",
        });
      }

      setSuccess("Order created successfully! Redirecting to order details...");

      // Redirect to order details
      setTimeout(() => {
        navigate(`/orders/${response.id}`);
      }, 2000);
    } catch (err) {
      console.error("Order creation failed:", err);
      setError(err.message || "Failed to create order");
    } finally {
      setSaving(false);
    }
  };

  if (loading && patients.length === 0) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: isMobile ? 1.5 : 4, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {/* Header with back button */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          onClick={() => navigate("/orders")}
          startIcon={<BackIcon />}
          variant="outlined"
          size="small"
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
          Create New Order
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Snackbar for non-critical messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      <Paper elevation={0} sx={{ p: isMobile ? 2 : 4, borderRadius: 3, border: "1px solid #E2E8F0" }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={isMobile ? 2 : 4}>
            {/* Patient Selection Section */}
            <Grid  size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h5" color="primary">
                      Patient Information
                    </Typography>
                  }
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={isMobile ? 1.5 : 3} alignItems="flex-end">
                    <Grid  size={{ xs: 12, md: 9 }}>
                      {!showNewPatient ? (
                        <Autocomplete
                          fullWidth
                          filterOptions={filterPatients}
                          options={patients}
                          getOptionLabel={(patient) => {
                            if (!patient) return "";
                            const dob = patient.date_of_birth
                              ? new Date(patient.date_of_birth).toLocaleDateString()
                              : "N/A";
                            return `${patient.first_name || ""} ${patient.last_name || ""} (${dob})`;
                          }}
                          value={
                            patients.find(
                              (p) => p.id === formValues.patient_id
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            setFormValues((prev) => ({
                              ...prev,
                              patient_id: newValue ? newValue.id : "",
                            }));
                          }}
                          disabled={loading || saving}
                          // ADD THESE PROPS FOR LARGER DROPDOWN:
                          ListboxProps={{
                            style: {
                              maxHeight: "400px", // Increase dropdown height
                              fontSize: "16px", // Larger text
                            },
                          }}
                          PaperComponent={({ children, ...other }) => (
                            <Paper
                              {...other}
                              style={{
                                minWidth: isMobile ? "100%" : "500px", // Minimum width for dropdown
                                maxWidth: "800px", // Maximum width for dropdown
                              }}
                            >
                              {children}
                            </Paper>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Patient *"
                              error={!!validationErrors.patient_id}
                              helperText={validationErrors.patient_id}
                              sx={{
                                bgcolor: "white",
                                // MAKE INPUT FIELD LARGER:
                                "& .MuiInputBase-root": {
                                  minHeight: "60px", // Taller input field
                                  fontSize: "16px", // Larger text in input
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "16px", // Larger label text
                                },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                style: {
                                  fontSize: "16px", // Larger input text
                                  minHeight: "60px", // Taller input
                                },
                              }}
                            />
                          )}
                          renderOption={(props, patient) => (
                            <li
                              {...props}
                              style={{
                                padding: "12px 16px", // More padding for each option
                                minHeight: "60px", // Taller options
                                borderBottom: "1px solid #f0f0f0", // Visual separator
                              }}
                            >
                              <Box sx={{ width: "100%" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    mb: 0.5,
                                  }}
                                >
                                  {patient.first_name} {patient.last_name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{
                                    fontSize: "14px",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  DOB:{" "}
                                  {patient.date_of_birth
                                    ? new Date(
                                        patient.date_of_birth
                                      ).toLocaleDateString()
                                    : "N/A"}
                                  {patient.phone_number &&
                                    ` • ${patient.phone_number}`}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          noOptionsText="No patients found. Click 'New Patient' to add one."
                        />
                      ) : (
                        <Card
                          variant="outlined"
                          sx={{ mt: 2, bgcolor: "#fafafa" }}
                        >
                          <CardHeader
                            title="Add New Patient"
                            avatar={<PersonIcon color="primary" />}
                            sx={{ pb: 1 }}
                          />
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="First Name"
                                  name="first_name"
                                  required
                                  value={newPatient.first_name}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Last Name"
                                  name="last_name"
                                  required
                                  value={newPatient.last_name}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Date of Birth"
                                  name="date_of_birth"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={newPatient.date_of_birth}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Gender</InputLabel>
                                  <Select
                                    name="gender"
                                    value={newPatient.gender}
                                    onChange={handlePatientChange}
                                    label="Gender"
                                    disabled={saving}
                                  >
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <TextField
                                  fullWidth
                                  label="Phone Number"
                                  name="phone_number"
                                  value={newPatient.phone_number}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <TextField
                                  fullWidth
                                  label="Address *"
                                  name="address"
                                  required
                                  value={newPatient.address}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <TextField
                                  fullWidth
                                  label="Address Notes (Gate Code, Apt #, etc.)"
                                  name="address_notes"
                                  value={newPatient.address_notes}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="City *"
                                  name="city"
                                  required
                                  value={newPatient.city}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 3 }}>
                                <TextField
                                  fullWidth
                                  label="State *"
                                  name="state"
                                  required
                                  value={newPatient.state}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 3 }}>
                                <TextField
                                  fullWidth
                                  label="ZIP Code *"
                                  name="zip_code"
                                  required
                                  value={newPatient.zip_code}
                                  onChange={handlePatientChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={2}
                                  mt={2}
                                >
                                  <Button
                                    variant="outlined"
                                    onClick={() => setShowNewPatient(false)}
                                    disabled={saving}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    onClick={handleCreatePatient}
                                    disabled={
                                      saving ||
                                      !newPatient.first_name?.trim() ||
                                      !newPatient.last_name?.trim() ||
                                      !newPatient.address?.trim() ||
                                      !newPatient.city?.trim() ||
                                      !newPatient.state?.trim() ||
                                      !newPatient.zip_code?.trim()
                                    }
                                    startIcon={<SaveIcon />}
                                  >
                                    {saving ? "Saving..." : "Save Patient"}
                                  </Button>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>

                    {!showNewPatient && (
                      <Grid  size={{ xs: 12, md: 3 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setShowNewPatient(true)}
                          disabled={loading || saving}
                          sx={{ height: 56 }}
                        >
                          New Patient
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Physician Selection Section */}
            <Grid  size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h5" color="primary">
                      Physician Information
                    </Typography>
                  }
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={isMobile ? 1.5 : 3} alignItems="flex-end">
                    <Grid  size={{ xs: 12, md: 9 }}>
                      {!showNewPhysician ? (
                        <Autocomplete
                          fullWidth
                          filterOptions={filterPhysicians}
                          options={physicians}
                          getOptionLabel={(physician) => {
                            if (!physician) return "";
                            const facility = physician.facility?.company_name || physician.facility_name;
                            return `Dr. ${physician.first_name || ""} ${physician.last_name || ""}${facility ? ` (${facility})` : ""}`;
                          }}
                          value={
                            physicians.find(
                              (p) => p.id === formValues.ordering_physician_id
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            setFormValues((prev) => ({
                              ...prev,
                              ordering_physician_id: newValue
                                ? newValue.id
                                : "",
                            }));
                          }}
                          disabled={loading || saving}
                          // ADD THESE PROPS FOR LARGER DROPDOWN:
                          ListboxProps={{
                            style: {
                              maxHeight: "400px", // Increase dropdown height
                              fontSize: "16px", // Larger text
                            },
                          }}
                          PaperComponent={({ children, ...other }) => (
                            <Paper
                              {...other}
                              style={{
                                minWidth: isMobile ? "100%" : "500px", // Minimum width for dropdown
                                maxWidth: "800px", // Maximum width for dropdown
                              }}
                            >
                              {children}
                            </Paper>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Ordering Physician"
                              sx={{
                                bgcolor: "white",
                                // MAKE INPUT FIELD LARGER:
                                "& .MuiInputBase-root": {
                                  minHeight: "60px", // Taller input field
                                  fontSize: "16px", // Larger text in input
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "16px", // Larger label text
                                },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                style: {
                                  fontSize: "16px", // Larger input text
                                  minHeight: "60px", // Taller input
                                },
                              }}
                            />
                          )}
                          renderOption={(props, physician) => (
                            <li
                              {...props}
                              style={{
                                padding: "12px 16px", // More padding for each option
                                minHeight: "70px", // Taller options
                                borderBottom: "1px solid #f0f0f0", // Visual separator
                              }}
                            >
                              <Box sx={{ width: "100%" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    mb: 0.5,
                                  }}
                                >
                                  Dr. {physician.first_name}{" "}
                                  {physician.last_name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{
                                    fontSize: "14px",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {physician.specialty &&
                                    `${physician.specialty} • `}
                                  {physician.facility?.company_name ||
                                    physician.facility_name ||
                                    "No facility"}
                                  {physician.npi && ` • NPI: ${physician.npi}`}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          noOptionsText="No physicians found. Click 'New Physician' to add one."
                        />
                      ) : (
                        <Card
                          variant="outlined"
                          sx={{ mt: 2, bgcolor: "#fafafa" }}
                        >
                          <CardHeader
                            title="Add New Physician"
                            avatar={<PhysicianIcon color="primary" />}
                            sx={{ pb: 1 }}
                          />
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="First Name"
                                  name="first_name"
                                  required
                                  value={newPhysician.first_name}
                                  onChange={handlePhysicianChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Last Name"
                                  name="last_name"
                                  required
                                  value={newPhysician.last_name}
                                  onChange={handlePhysicianChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <TextField
                                    fullWidth
                                    label="NPI Number"
                                    name="npi"
                                    value={newPhysician.npi}
                                    onChange={handlePhysicianChange}
                                    size="small"
                                    placeholder="10-digit number"
                                    inputProps={{
                                      maxLength: 10,
                                      pattern: "[0-9]*",
                                    }}
                                    disabled={saving}
                                  />
                                  <IconButton
                                    onClick={() =>
                                      window.open(
                                        "https://npiregistry.cms.hhs.gov/",
                                        "_blank"
                                      )
                                    }
                                    size="small"
                                    title="Look up NPI numbers"
                                    disabled={saving}
                                  >
                                    <ExternalLinkIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                <FormHelperText>
                                  <Link
                                    href="https://npiregistry.cms.hhs.gov/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <InfoIcon fontSize="small" />
                                    Look up NPI numbers
                                  </Link>
                                </FormHelperText>
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Facility Name"
                                  name="facility_name"
                                  value={newPhysician.facility_name}
                                  onChange={handlePhysicianChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Phone Number"
                                  name="phone_number"
                                  value={newPhysician.phone_number}
                                  onChange={handlePhysicianChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Email"
                                  name="email"
                                  type="email"
                                  value={newPhysician.email}
                                  onChange={handlePhysicianChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={2}
                                  mt={2}
                                >
                                  <Button
                                    variant="outlined"
                                    onClick={() => setShowNewPhysician(false)}
                                    disabled={saving}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    onClick={handleCreatePhysician}
                                    disabled={
                                      saving ||
                                      !newPhysician.first_name?.trim() ||
                                      !newPhysician.last_name?.trim() ||
                                      !canCreatePhysicians
                                    }
                                    startIcon={<SaveIcon />}
                                  >
                                    {saving ? "Saving..." : "Save Physician"}
                                  </Button>
                                </Box>
                                {!canCreatePhysicians && (
                                  <Alert severity="info" sx={{ mt: 2 }}>
                                    Contact your administrator to create
                                    physicians
                                  </Alert>
                                )}
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>

                    {!showNewPhysician && (
                      <Grid  size={{ xs: 12, md: 3 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setShowNewPhysician(true)}
                          disabled={loading || saving || !canCreatePhysicians}
                          sx={{ height: 56 }}
                        >
                          New Physician
                        </Button>
                        {!canCreatePhysicians && (
                          <FormHelperText sx={{ mt: 1, textAlign: "center" }}>
                            Contact admin to create physicians
                          </FormHelperText>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Facility Selection Section - FIXED for facility admins */}
            <Grid  size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h5" color="primary">
                      Facility Information
                    </Typography>
                  }
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={isMobile ? 1.5 : 3} alignItems="flex-end">
                    <Grid  size={{ xs: 12, md: 9 }}>
                      {!showNewFacility ? (
                        <FormControl
                          fullWidth
                          required
                          error={!!validationErrors.facility_id}
                        >
                          <InputLabel id="facility-label">Facility</InputLabel>
                          <Select
                            labelId="facility-label"
                            name="facility_id"
                            value={formValues.facility_id}
                            onChange={handleChange}
                            label="Facility"
                            disabled={
                              loading ||
                              saving ||
                              user?.role === "facility_admin"
                            }
                            sx={{ bgcolor: "white" }}
                          >
                            {facilities.length === 0 && (
                              <MenuItem disabled value="">
                                <em>
                                  No facilities available. Please create one.
                                </em>
                              </MenuItem>
                            )}
                            {facilities.map((facility) => (
                              <MenuItem key={facility.id} value={facility.id}>
                                {facility.company_name} ({facility.address})
                              </MenuItem>
                            ))}
                          </Select>
                          {user?.role === "facility_admin" && (
                            <FormHelperText>
                              Your facility is automatically selected
                            </FormHelperText>
                          )}
                          {facilities.length === 0 && (
                            <FormHelperText error>
                              No facilities found. Please create a new facility.
                            </FormHelperText>
                          )}
                          {validationErrors.facility_id && (
                            <FormHelperText error>
                              {validationErrors.facility_id}
                            </FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <Card
                          variant="outlined"
                          sx={{ mt: 2, bgcolor: "#fafafa" }}
                        >
                          <CardHeader
                            title="Add New Facility"
                            avatar={<BusinessIcon color="primary" />}
                            sx={{ pb: 1 }}
                          />
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Company Name"
                                  name="company_name"
                                  required
                                  value={newFacility.company_name}
                                  onChange={handleFacilityChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Phone Number"
                                  name="phone_number"
                                  required
                                  value={newFacility.phone_number}
                                  onChange={handleFacilityChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <TextField
                                  fullWidth
                                  label="Address"
                                  name="address"
                                  required
                                  value={newFacility.address}
                                  onChange={handleFacilityChange}
                                  size="small"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label="Email (Optional)"
                                  name="email"
                                  type="email"
                                  value={newFacility.email}
                                  onChange={handleFacilityChange}
                                  size="small"
                                  helperText="Optional - some facilities only use fax/phone"
                                  disabled={saving}
                                />
                              </Grid>
                              <Grid  size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Subscription Type</InputLabel>
                                  <Select
                                    name="subscription_type"
                                    value={newFacility.subscription_type}
                                    onChange={handleFacilityChange}
                                    label="Subscription Type"
                                    disabled={saving}
                                  >
                                    <MenuItem value="basic">Basic</MenuItem>
                                    <MenuItem value="standard">
                                      Standard
                                    </MenuItem>
                                    <MenuItem value="premium">Premium</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid  size={{ xs: 12 }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={2}
                                  mt={2}
                                >
                                  <Button
                                    variant="outlined"
                                    onClick={() => setShowNewFacility(false)}
                                    disabled={saving}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    onClick={handleCreateFacility}
                                    disabled={
                                      saving ||
                                      !newFacility.company_name?.trim() ||
                                      !newFacility.phone_number?.trim() ||
                                      !newFacility.address?.trim() ||
                                      !canCreateFacilities
                                    }
                                    startIcon={<SaveIcon />}
                                  >
                                    {saving ? "Saving..." : "Save Facility"}
                                  </Button>
                                </Box>
                                {!canCreateFacilities && (
                                  <Alert severity="info" sx={{ mt: 2 }}>
                                    Only super administrators can create
                                    facilities
                                  </Alert>
                                )}
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>

                    {!showNewFacility && canCreateFacilities && (
                      <Grid  size={{ xs: 12, md: 3 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setShowNewFacility(true)}
                          disabled={loading || saving}
                          sx={{ height: 56 }}
                        >
                          New Facility
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Order Details Section */}
            <Grid  size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h5" color="primary">
                      Order Details
                    </Typography>
                  }
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={isMobile ? 1.5 : 3}>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Place of Service</InputLabel>
                        <Select
                          name="place_of_service"
                          value={formValues.place_of_service}
                          onChange={handleChange}
                          label="Place of Service"
                          disabled={loading || saving}
                          sx={{ bgcolor: "white" }}
                        >
                          <MenuItem value="Home">Home</MenuItem>
                          <MenuItem value="Facility">Facility</MenuItem>
                          <MenuItem value="Office">Office</MenuItem>
                          <MenuItem value="Hospital">Hospital</MenuItem>
                          <MenuItem value="Nursing Home">Nursing Home</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Ordering Health Company"
                        name="ordering_health_company"
                        value={formValues.ordering_health_company}
                        onChange={handleChange}
                        disabled={loading || saving}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>

                    <Grid  size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Scheduled Date"
                        name="scheduled_date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formValues.scheduled_date}
                        onChange={handleChange}
                        disabled={loading || saving}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="Scheduled Time (From)"
                        name="scheduled_time"
                        type="time"
                        InputLabelProps={{ shrink: true }}
                        value={formValues.scheduled_time}
                        onChange={handleChange}
                        disabled={loading || saving}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="Scheduled Time (To)"
                        name="scheduled_time_end"
                        type="time"
                        InputLabelProps={{ shrink: true }}
                        value={formValues.scheduled_time_end}
                        onChange={handleChange}
                        disabled={loading || saving}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>

                    {/* Medical Professional Selection - FIXED for different user roles */}
                    {canAssignMedicalProfessionals && (
                      <Grid  size={{ xs: 12 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                          <FormControl fullWidth sx={{ flexGrow: 1, mr: 2 }}>
                            <InputLabel>Assign Medical Professional</InputLabel>
                            <Select
                              name="medical_professional_id"
                              value={formValues.medical_professional_id}
                              onChange={handleChange}
                              label="Assign Medical Professional"
                              disabled={loading || saving}
                              sx={{ bgcolor: "white" }}
                            >
                              <MenuItem value="">
                                <em>None (Assign Later)</em>
                              </MenuItem>
                              {medicalProfessionals.map((mp) => (
                                <MenuItem key={mp.id} value={mp.id}>
                                  {mp.user?.full_name || mp.id} (
                                  {mp.mp_category})
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              Optional: Assign a medical professional now, or do
                              it later via route optimization
                            </FormHelperText>
                          </FormControl>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setShowCustomMedicalProfessionalDialog(true)
                            }
                            disabled={loading || saving}
                            sx={{ height: 56 }}
                          >
                            Add New
                          </Button>
                        </Box>
                      </Grid>
                    )}

                    <Grid  size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Special Notes"
                        name="special_notes"
                        value={formValues.special_notes}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        disabled={loading || saving}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Diagnoses and Exams Row */}
            <Grid  size={{ xs: 12 }}>
              <Grid container spacing={isMobile ? 1.5 : 3}>
                {/* Diagnoses Section */}
                <Grid  size={{ xs: 12, lg: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{ borderRadius: 2, height: "100%" }}
                  >
                    <CardHeader
                      title={
                        <Typography variant="h5" color="primary">
                          Diagnoses
                        </Typography>
                      }
                      sx={{ pb: 1 }}
                      action={
                        <Button
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => setShowCustomDiagnosisDialog(true)}
                          size="small"
                          disabled={saving}
                        >
                          Add Custom
                        </Button>
                      }
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <Autocomplete
                        multiple
                        options={diagnoses}
                        getOptionLabel={(option) =>
                          `${option.code} - ${option.description}`
                        }
                        value={formValues.selected_diagnoses}
                        onChange={handleDiagnosisChange}
                        disabled={loading || saving}
                        noOptionsText="No diagnoses found. Click 'Add Custom' to create a new one."
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Diagnoses"
                            placeholder="Select diagnoses"
                            sx={{ bgcolor: "white" }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.id}
                              label={`${option.code} - ${option.description}`}
                              {...getTagProps({ index })}
                              color={index === 0 ? "primary" : "default"}
                              sx={{ m: 0.5 }}
                            />
                          ))
                        }
                      />
                      <FormHelperText sx={{ mt: 1 }}>
                        First selected diagnosis will be marked as primary
                      </FormHelperText>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Exams Section with proper validation display */}
                <Grid  size={{ xs: 12, lg: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{ borderRadius: 2, height: "100%" }}
                  >
                    <CardHeader
                      title={
                        <Typography variant="h5" color="primary">
                          Exams *
                        </Typography>
                      }
                      sx={{ pb: 1 }}
                      action={
                        <Button
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => setShowCustomExamDialog(true)}
                          size="small"
                          disabled={saving}
                        >
                          Add Custom
                        </Button>
                      }
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <FormControl
                        fullWidth
                        error={!!validationErrors.selected_exams}
                        required
                      >
                        <Autocomplete
                          multiple
                          options={examTypes}
                          getOptionLabel={(option) => option.name}
                          value={formValues.selected_exams}
                          onChange={handleExamChange}
                          disabled={loading || saving}
                          noOptionsText="No exams found. Click 'Add Custom' to create a new one."
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Exams *"
                              placeholder="Select required exams"
                              sx={{ bgcolor: "white" }}
                              error={!!validationErrors.selected_exams}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box>
                                <Typography variant="body1">
                                  {option.name}
                                </Typography>
                                {option.description && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {option.description}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={option.id}
                                label={option.name}
                                {...getTagProps({ index })}
                                color="primary"
                                size="small"
                              />
                            ))
                          }
                        />

                        {/* Display validation error */}
                        {validationErrors.selected_exams && (
                          <FormHelperText error>
                            {validationErrors.selected_exams}
                          </FormHelperText>
                        )}

                        {/* Display current exam count */}
                        <FormHelperText>
                          {formValues.selected_exams.length > 0
                            ? `${formValues.selected_exams.length} exam(s) selected`
                            : "At least one exam is required *"}
                        </FormHelperText>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Files Section with proper validation display */}
                <Grid  size={{ xs: 12 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardHeader
                      title={
                        <Box display="flex" alignItems="center" gap={1}>
                          <AttachFileIcon color="primary" />
                          <Typography variant="h5" color="primary">
                            Attach Documents (Optional)
                          </Typography>
                        </Box>
                      }
                      sx={{ pb: 1 }}
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Secure File Upload:</strong> Files are
                          encrypted and stored securely in compliance with HIPAA
                          regulations. Maximum file size: 50MB. Supported
                          formats: PDF, JPG, PNG, TIFF, DOC, DOCX, XLS, XLSX,
                          TXT, RTF
                        </Typography>
                      </Alert>

                      <Box>
                        <input
                          accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.doc,.docx,.xls,.xlsx,.txt,.rtf"
                          style={{ display: "none" }}
                          id="file-upload"
                          multiple
                          type="file"
                          onChange={handleFileSelect}
                          disabled={saving || uploadingFiles}
                        />
                        <label htmlFor="file-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            disabled={saving || uploadingFiles}
                            fullWidth
                            sx={{ mb: 2 }}
                          >
                            Select Files to Upload
                          </Button>
                        </label>

                        {selectedFiles.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Selected Files ({selectedFiles.length}):
                            </Typography>
                            {selectedFiles.map((file, index) => (
                              <Card
                                key={index}
                                variant="outlined"
                                sx={{ mb: 1, p: 1 }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <FileIcon color="action" />
                                    <Box>
                                      <Typography variant="body2">
                                        {file.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {(file.size / 1024).toFixed(2)} KB
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFile(index)}
                                    disabled={saving || uploadingFiles}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Card>
                            ))}
                          </Box>
                        )}

                        {uploadingFiles && (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={2}
                          >
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                              Uploading files...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Grid  size={{ xs: 12 }}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    loading ||
                    saving ||
                    success ===
                      "Order created successfully! Redirecting to order details..."
                  }
                  startIcon={<SaveIcon />}
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  {saving ? "Creating Order..." : "Create Order"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Custom Exam Dialog */}
      <Dialog
        open={showCustomExamDialog}
        onClose={() => !saving && setShowCustomExamDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Exam</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Exam Name"
                required
                value={newCustomExam.name}
                onChange={(e) =>
                  setNewCustomExam({ ...newCustomExam, name: e.target.value })
                }
                disabled={saving}
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={newCustomExam.description}
                onChange={(e) =>
                  setNewCustomExam({
                    ...newCustomExam,
                    description: e.target.value,
                  })
                }
                disabled={saving}
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={newCustomExam.duration || ""}
                onChange={(e) =>
                  setNewCustomExam({
                    ...newCustomExam,
                    duration: e.target.value
                      ? parseInt(e.target.value, 10)
                      : null,
                  })
                }
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCustomExamDialog(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomExam}
            variant="contained"
            disabled={!newCustomExam.name?.trim() || saving}
            startIcon={saving ? null : <AddIcon />}
          >
            {saving ? "Adding..." : "Add Exam"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Diagnosis Dialog */}
      <Dialog
        open={showCustomDiagnosisDialog}
        onClose={() => !saving && setShowCustomDiagnosisDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Diagnosis</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid  size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                label="Code"
                required
                value={newCustomDiagnosis.code}
                onChange={(e) =>
                  setNewCustomDiagnosis({
                    ...newCustomDiagnosis,
                    code: e.target.value,
                  })
                }
                disabled={saving}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 7 }}>
              <TextField
                fullWidth
                label="Description"
                required
                value={newCustomDiagnosis.description}
                onChange={(e) =>
                  setNewCustomDiagnosis({
                    ...newCustomDiagnosis,
                    description: e.target.value,
                  })
                }
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCustomDiagnosisDialog(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomDiagnosis}
            variant="contained"
            disabled={
              !newCustomDiagnosis.code?.trim() ||
              !newCustomDiagnosis.description?.trim() ||
              saving
            }
            startIcon={saving ? null : <AddIcon />}
          >
            {saving ? "Adding..." : "Add Diagnosis"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Medical Professional Dialog */}
      <Dialog
        open={showCustomMedicalProfessionalDialog}
        onClose={() => !saving && setShowCustomMedicalProfessionalDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Medical Professional</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Personal Information Section */}
            <Grid  size={{ xs: 12 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Full Name *"
                required
                value={newCustomMedicalProfessional.full_name}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    full_name: e.target.value,
                  })
                }
                disabled={saving}
                error={!newCustomMedicalProfessional.full_name?.trim()}
                helperText={
                  !newCustomMedicalProfessional.full_name?.trim()
                    ? "Required field"
                    : ""
                }
              />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Username *"
                required
                value={newCustomMedicalProfessional.username}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    username: e.target.value,
                  })
                }
                disabled={saving}
                error={!newCustomMedicalProfessional.username?.trim()}
                helperText={
                  !newCustomMedicalProfessional.username?.trim()
                    ? "Required field"
                    : "For system login"
                }
              />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                required
                value={newCustomMedicalProfessional.email}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    email: e.target.value,
                  })
                }
                disabled={saving}
                error={!newCustomMedicalProfessional.email?.trim()}
                helperText={
                  !newCustomMedicalProfessional.email?.trim()
                    ? "Required field"
                    : ""
                }
              />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <FormControl
                fullWidth
                required
                error={!newCustomMedicalProfessional.mp_category}
              >
                <InputLabel>Category *</InputLabel>
                <Select
                  value={newCustomMedicalProfessional.mp_category}
                  onChange={(e) =>
                    setNewCustomMedicalProfessional({
                      ...newCustomMedicalProfessional,
                      mp_category: e.target.value,
                    })
                  }
                  label="Category *"
                  disabled={saving}
                >
                  <MenuItem value="nurse">Nurse</MenuItem>
                  <MenuItem value="phlebotomist">Phlebotomist</MenuItem>
                  <MenuItem value="technician">Technician</MenuItem>
                  <MenuItem value="doctor">Physician Assistant</MenuItem>
                  <MenuItem value="therapist">Therapist</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {!newCustomMedicalProfessional.mp_category && (
                  <FormHelperText error>
                    Please select a category
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Professional Information Section */}
            <Grid sx={{ mt: 2 }} size={{ xs: 12 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Professional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Medical License Number"
                value={newCustomMedicalProfessional.license_number}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    license_number: e.target.value,
                  })
                }
                disabled={saving}
                helperText="Optional - Enter if applicable"
              />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="NPI Number"
                value={newCustomMedicalProfessional.npi_number}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    npi_number: e.target.value,
                  })
                }
                disabled={saving}
                helperText="Optional - Not all professionals have NPIs"
                inputProps={{
                  maxLength: 10,
                  pattern: "[0-9]*",
                }}
              />
            </Grid>

            {/* Security Information Section */}
            <Grid sx={{ mt: 2 }} size={{ xs: 12 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Security Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                required
                value={newCustomMedicalProfessional.password}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    password: e.target.value,
                  })
                }
                disabled={saving}
                error={!newCustomMedicalProfessional.password?.trim()}
                helperText={
                  !newCustomMedicalProfessional.password?.trim()
                    ? "Required field"
                    : "Minimum 6 characters"
                }
              />
            </Grid>

            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Confirm Password *"
                type="password"
                required
                value={newCustomMedicalProfessional.confirm_password}
                onChange={(e) =>
                  setNewCustomMedicalProfessional({
                    ...newCustomMedicalProfessional,
                    confirm_password: e.target.value,
                  })
                }
                error={
                  newCustomMedicalProfessional.confirm_password !== "" &&
                  newCustomMedicalProfessional.password !==
                    newCustomMedicalProfessional.confirm_password
                }
                helperText={
                  newCustomMedicalProfessional.confirm_password !== "" &&
                  newCustomMedicalProfessional.password !==
                    newCustomMedicalProfessional.confirm_password
                    ? "Passwords don't match"
                    : "Must match password above"
                }
                disabled={saving}
              />
            </Grid>

            {/* Info Alert */}
            <Grid  size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Only Name, Username, Email, Category,
                  and Password are required. License and NPI numbers are
                  optional as not all medical professionals have these
                  credentials.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowCustomMedicalProfessionalDialog(false)}
            disabled={saving}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomMedicalProfessional}
            variant="contained"
            disabled={
              saving ||
              !newCustomMedicalProfessional.full_name?.trim() ||
              !newCustomMedicalProfessional.username?.trim() ||
              !newCustomMedicalProfessional.email?.trim() ||
              !newCustomMedicalProfessional.password?.trim() ||
              !newCustomMedicalProfessional.mp_category ||
              newCustomMedicalProfessional.password !==
                newCustomMedicalProfessional.confirm_password
            }
            startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {saving ? "Creating..." : "Add Medical Professional"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderCreate;
